import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "searchInput", "results", "form", "column"]
  static values = {
    url: String,
    endpoint: String,
    jwt: String,
    offset: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    hasMore: { type: Boolean, default: true }
  }

  connect() {
    this.debouncedSearch = this.debounce(this.performSearch.bind(this), 300)
    this.currentQuery = ""
    this.columns = [[], [], []]
    this.currentColumn = 0

    // Set up intersection observer for infinite scroll
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.hasMoreValue && !this.loadingValue) {
            this.loadMore()
          }
        })
      },
      { threshold: 0.5 }
    )
  }

  disconnect() {
    this.observer.disconnect()
  }

  async search(event) {
    this.currentQuery = event.target.value
    this.offsetValue = 0
    this.hasMoreValue = true
    this.columns = [[], [], []]
    this.currentColumn = 0
    this.resultsTarget.innerHTML = this.generateColumnsHTML()
    this.debouncedSearch(this.currentQuery)
  }

  async performSearch(query, append = false) {
    if (!query || query.length < 2) {
      this.resultsTarget.innerHTML = ""
      return
    }

    if (this.loadingValue) return

    this.loadingValue = true
    const params = new URLSearchParams({
      jwt: this.jwtValue,
      q: query,
      limit: 12,
      offset: this.offsetValue,
      rating: 'g'
    })

    try {
      const response = await fetch(`${this.endpointValue}?${params}`)
      const data = await response.json()

      if (!append) {
        this.columns = [[], [], []]
        this.currentColumn = 0
        this.resultsTarget.innerHTML = this.generateColumnsHTML()
      }

      this.displayResults(data.gifs, append)

      this.hasMoreValue = data.pagination && data.pagination.total_count > this.offsetValue + data.gifs.length
      this.offsetValue += data.gifs.length

      if (this.hasMoreValue) {
        this.observeLastItems()
      }
    } catch (error) {
      console.error("Error fetching GIFs:", error)
    } finally {
      this.loadingValue = false
    }
  }

  generateColumnsHTML() {
    return `
      <div class="masonry-column space-y-4" data-giphy-search-target="column"></div>
      <div class="masonry-column space-y-4" data-giphy-search-target="column"></div>
      <div class="masonry-column space-y-4" data-giphy-search-target="column"></div>
    `
  }

  displayResults(gifs, append = false) {
    gifs.forEach((gif, index) => {
      const columnIndex = index % 3
      const column = this.columnTargets[columnIndex]
      const gifElement = this.createGifElement(gif)
      column.appendChild(gifElement)
    })
  }

  createGifElement(gif) {
    const div = document.createElement('div')
    div.className = 'cursor-pointer hover:opacity-75 transition-opacity w-full'
    div.dataset.action = 'click->giphy-search#selectGif'
    div.dataset.url = gif.images.fixed_height

    const img = document.createElement('img')
    img.src = gif.images.fixed_height
    img.alt = gif.title
    img.loading = 'lazy'
    img.className = 'rounded-lg w-full'

    div.appendChild(img)
    return div
  }

  observeLastItems() {
    this.columnTargets.forEach(column => {
      const items = column.children
      if (items.length > 0) {
        this.observer.observe(items[items.length - 1])
      }
    })
  }

  async loadMore() {
    if (this.loadingValue || !this.hasMoreValue) return
    await this.performSearch(this.currentQuery, true)
  }

  selectGif(event) {
    const gifUrl = event.currentTarget.dataset.url
    this.inputTarget.value = gifUrl
    this.formTarget.requestSubmit()
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }
}